import { useEffect, useState, Fragment } from 'react';
import {
  Download, AlertCircle, Loader2,
  ChevronDown, ChevronUp, Copy, Check,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import Reveal from '../components/animations/Reveal';
import CountUp from '../components/animations/CountUp';
import {
  getAnalysisStatus,
  getAnalysisResults,
  getDownloadUrl,
  pollUntilDone,
} from '../api/analysisApi';

// Crickmore Cry nomenclature: Cry1Aa1 -> rank1(1) + rank2(A) = family "Cry1A".
// Best-effort grouping for display only — not a substitute for real toxin classification.
// Used only as a fallback if the backend didn't already supply cry_family.
const deriveFamily = (id) => {
  const m = /^cry(\d+)([a-z])/i.exec(id || '');
  return m ? `Cry${m[1]}${m[2].toUpperCase()}` : 'Other';
};

// ── Table column definitions ─────────────────────────────────────────────────
const TABLE_HEADERS = [
  'Gene ID',
  'Family',
  'Length',
  'Coverage',
  'Identity',
  'Accession No.',
  'Strain',
  'Gene Status',
  'E-value',
  '', // Details toggle
];
// ────────────────────────────────────────────────────────────────────────────

const ResultsPage = ({ setActiveTab }) => {
  const [status, setStatus]           = useState('loading'); // loading | no-job | pending | error | ready
  const [step, setStep]               = useState(null);
  const [data, setData]               = useState(null);
  const [error, setError]             = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [copiedRow, setCopiedRow]     = useState(null);
  const [pageSize, setPageSize]       = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const changePageSize = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const copySequence = (text, index) => {
    navigator.clipboard.writeText(text || '');
    setCopiedRow(index);
    setTimeout(() => setCopiedRow((cur) => (cur === index ? null : cur)), 1500);
  };

  useEffect(() => {
    let cancelled = false;
    const jobId = sessionStorage.getItem('cry_last_job_id');

    if (!jobId) {
      setStatus('no-job');
      return;
    }

    (async () => {
      try {
        const current = await getAnalysisStatus(jobId);

        if (current.status === 'failed') {
          if (!cancelled) {
            setStatus('error');
            setError(current.error || 'Analysis failed');
          }
          return;
        }

        if (current.status !== 'completed') {
          if (!cancelled) {
            setStatus('pending');
            setStep(current.step);
          }
          await pollUntilDone(jobId, {
            onStatusChange: (s) => {
              if (!cancelled) setStep(s.step);
            },
          });
        }

        const results = await getAnalysisResults(jobId);
        if (!cancelled) {
          setData(results);
          setStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError(err.message || 'Could not load results');
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const goToAnalysis = () => setActiveTab?.('analysis');

  // ── Loading / pending ──────────────────────────────────────────────────────
  if (status === 'loading' || status === 'pending') {
    return (
      <div className="py-24 text-center">
        <Loader2 className="w-8 h-8 text-brand mx-auto mb-4 animate-spin" />
        <p className="text-slate-600">
          {status === 'pending'
            ? `Analysis in progress (${step || 'starting'})...`
            : 'Loading results...'}
        </p>
        <p className="text-sm text-slate-400 mt-1">This page updates automatically when it's done.</p>
      </div>
    );
  }

  // ── No job ─────────────────────────────────────────────────────────────────
  if (status === 'no-job') {
    return (
      <div className="py-24 text-center">
        <p className="text-slate-600 mb-4">No analysis has been run yet.</p>
        <button
          onClick={goToAnalysis}
          className="brand-btn px-6 py-3 rounded-xl hover:scale-105 transition-transform duration-300"
        >
          Go to Analysis
        </button>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="py-24 text-center max-w-md mx-auto">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-slate-700 mb-1">Couldn't load results</p>
        <p className="text-sm text-red-600 mb-6">{error}</p>
        <button
          onClick={goToAnalysis}
          className="brand-btn px-6 py-3 rounded-xl hover:scale-105 transition-transform duration-300"
        >
          Back to Analysis
        </button>
      </div>
    );
  }

  const { rows, hitCount, sampleName, jobId } = data;

  // ── No hits ────────────────────────────────────────────────────────────────
  if (hitCount === 0) {
    return (
      <div className="py-24 text-center max-w-md mx-auto">
        <p className="text-slate-700 mb-1">No Cry toxin matches found for {sampleName}.</p>
        <p className="text-sm text-slate-500 mb-6">
          Try lowering the Sequence Identity threshold and re-running the analysis.
        </p>
        <button
          onClick={goToAnalysis}
          className="brand-btn px-6 py-3 rounded-xl hover:scale-105 transition-transform duration-300"
        >
          Back to Analysis
        </button>
      </div>
    );
  }

  // ── Stats ──────────────────────────────────────────────────────────────────
  const families        = rows.map((r) => r.cry_family || deriveFamily(r.cry_toxin_id));
  const distinctFamilies = new Set(families).size;
  const avgIdentity     = rows.reduce((sum, r) => sum + parseFloat(r.identity_pct), 0) / rows.length;
  const bestHit         = rows.reduce(
    (best, r) => (parseFloat(r.evalue) < parseFloat(best.evalue) ? r : best),
    rows[0],
  );

  const stats = [
    { label: 'Total Hits Found',       value: String(hitCount) },
    { label: 'Cry Families Detected',  value: String(distinctFamilies) },
    { label: 'Avg. Identity',          value: `${avgIdentity.toFixed(1)}%` },
    { label: 'Best Match E-value',     value: bestHit.evalue },
  ];

  const fastaUrl = getDownloadUrl(jobId, `${sampleName}.fasta`);
  const tsvUrl   = getDownloadUrl(jobId, 'CryToxin.tsv');

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const startIdx   = (safePage - 1) * pageSize;
  const endIdx     = Math.min(startIdx + pageSize, rows.length);
  const pageRows   = rows.slice(startIdx, endIdx);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Title */}
        <Reveal>
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-6">
              Analysis <span className="text-brand">Results</span>
            </h2>
            <p className="text-xl text-slate-600">
              Cry toxin gene identification results for {sampleName}
            </p>
          </div>
        </Reveal>

        {/* Stat cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => (
            <Reveal key={index} delay={index * 100}>
              <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="font-display text-3xl font-bold text-brand mb-2">
                  <CountUp value={stat.value} duration={1200} />
                </div>
                <div className="text-slate-500">{stat.label}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Results table */}
        <Reveal delay={150}>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

            {/* Table header bar */}
            <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-display text-xl font-semibold text-slate-900">
                Identified Cry Toxin Genes
              </h3>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <label htmlFor="page-size">Rows per page:</label>
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => changePageSize(Number(e.target.value))}
                  className="border border-slate-300 rounded-lg px-2 py-1 text-slate-700 focus:border-brand focus:outline-none"
                >
                  {[50, 100, 150, 200, 500].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Scrollable table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200">
                  {pageRows.map((gene, i) => {
                    const index    = startIdx + i; // global index keeps expandedRow correct across pages
                    const isOpen   = expandedRow === index;
                    const identity = parseFloat(gene.identity_pct);
                    const coverage = gene.coverage_pct != null ? parseFloat(gene.coverage_pct) : null;
                    const family   = gene.cry_family || deriveFamily(gene.cry_toxin_id);

                    return (
                      <Fragment key={index}>
                        {/* Main row */}
                        <tr
                          className="hover:bg-slate-50 transition-colors animate-row-in"
                          style={{ animationDelay: `${300 + i * 90}ms` }}
                        >
                          {/* Gene ID */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-brand">
                            {gene.cry_toxin_id}
                          </td>

                          {/* Family */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {family}
                          </td>

                          {/* Length */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {gene.alignment_length} bp
                          </td>

                          {/* Coverage */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {coverage != null ? (
                              <div className="flex items-center gap-2">
                                
                                <span>{Math.round(coverage)}%</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>

                          {/* Identity */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-brand rounded-full animate-bar-grow"
                                  style={{
                                    '--bar-width': `${identity}%`,
                                    animationDelay: `${500 + i * 90}ms`,
                                  }}
                                />
                              </div>
                              <span>{identity}%</span>
                            </div>
                          </td>

                          {/* Accession No. */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {gene.acc_no ? (
                              <a
                                href={`https://www.ncbi.nlm.nih.gov/protein/${gene.acc_no}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-brand hover:underline font-medium"
                              >
                                {gene.acc_no}
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>

                          {/* Strain */}
                          <td className="px-6 py-4 text-sm text-slate-600 max-w-[180px] truncate" title={gene.strain || ''}>
                            {gene.strain ?? <span className="text-slate-400">—</span>}
                          </td>

                          {/* Gene Status */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {gene.gene_status ? (
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                  gene.gene_status === 'Complete'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-amber-100 text-amber-800'
                                }`}
                              >
                                {gene.gene_status}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>

                          {/* E-value */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {gene.evalue}
                          </td>

                          {/* Details toggle */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            <button
                              onClick={() => setExpandedRow(isOpen ? null : index)}
                              className="text-brand hover:underline transition-colors flex items-center gap-1"
                            >
                              {isOpen
                                ? <ChevronUp className="w-4 h-4" />
                                : <ChevronDown className="w-4 h-4" />}
                              Details
                            </button>
                          </td>
                        </tr>

                        {/* Expanded details row */}
                        {isOpen && (
                          <tr className="bg-slate-50">
                            <td colSpan={TABLE_HEADERS.length} className="px-6 py-4 text-sm text-slate-600">
                              <div className="grid md:grid-cols-2 gap-4 mb-3">
                                <div>
                                  <span className="font-semibold text-slate-700">Query contig region:</span>{' '}
                                  {gene.query_start}–{gene.query_end}
                                  {' '}(toxin gene {gene.cry_gene_start}–{gene.cry_gene_end})
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Toxin details:</span>{' '}
                                  {gene.toxin_details || '—'}
                                </div>
                                {gene.acc_no && (
                                  <div>
                                    <span className="font-semibold text-slate-700">Accession:</span>{' '}
                                    <a
                                      href={`https://www.ncbi.nlm.nih.gov/protein/${gene.acc_no}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-brand hover:underline"
                                    >
                                      {gene.acc_no}
                                    </a>
                                  </div>
                                )}
                                {gene.strain && (
                                  <div>
                                    <span className="font-semibold text-slate-700">Strain:</span>{' '}
                                    {gene.strain}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center justify-between mb-1">
                                <span className="font-semibold text-slate-700">
                                  Full toxin sequence ({(gene.cry_gene_sequence || '').length} bp):
                                </span>
                                <button
                                  onClick={() => copySequence(gene.cry_gene_sequence, index)}
                                  className="text-brand hover:underline text-xs flex items-center gap-1"
                                >
                                  {copiedRow === index
                                    ? <><Check className="w-3.5 h-3.5" /> Copied!</>
                                    : <><Copy className="w-3.5 h-3.5" /> Copy sequence</>}
                                </button>
                              </div>
                              <div className="font-mono text-xs bg-white border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto break-all">
                                {gene.cry_gene_sequence || '—'}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3 border-t border-slate-200 bg-slate-50">
              <p className="text-sm text-slate-500">
                Showing {rows.length === 0 ? 0 : startIdx + 1}–{endIdx} of {rows.length} hits
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:border-brand hover:text-brand disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:text-slate-600 transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-slate-600 px-2">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:border-brand hover:text-brand disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:text-slate-600 transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Download buttons */}
        <Reveal delay={200}>
          <div className="mt-12 text-center">
            <div className="space-y-4">
              <h3 className="font-display text-xl font-semibold text-slate-900">Download Results</h3>
              <div className="flex justify-center flex-wrap gap-4">
                <a
                  href={tsvUrl}
                  className="brand-btn px-6 py-3 rounded-xl hover:scale-105 transition-transform duration-300 flex items-center space-x-2"
                >
                  <Download className="w-5 h-5" />
                  <span>CryToxin.tsv</span>
                </a>
                <a
                  href={fastaUrl}
                  className="px-6 py-3 bg-white border border-brand text-brand rounded-xl hover:bg-brand-tint hover:scale-105 transition-all duration-300 flex items-center space-x-2"
                >
                  <Download className="w-5 h-5" />
                  <span>{sampleName}.fasta</span>
                </a>
              </div>
            </div>
          </div>
        </Reveal>

      </div>
    </div>
  );
};

export default ResultsPage;